import { useTranslation } from "react-i18next";

import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import type { Aggregation, Metric, Mode, Preset } from "./types";

export type ChartToolbarProps = {
  activePreset: Preset | null;
  onPreset: (p: Preset) => void;
  aggregation: Aggregation;
  onAggregation: (a: Aggregation) => void;
  /** Omit `metric`/`onMetric` to hide the Pieces/Revenue toggle. */
  metric?: Metric;
  onMetric?: (m: Metric) => void;
  mode: Mode;
  onMode: (m: Mode) => void;
};

const PRESETS: Preset[] = ["1Y", "3Y", "All"];

function Section({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-muted-foreground text-[10px] font-medium tracking-wider uppercase">
        {label}
      </span>
      {children}
    </div>
  );
}

export function ChartToolbar({
  activePreset,
  onPreset,
  aggregation,
  onAggregation,
  metric,
  onMetric,
  mode,
  onMode,
}: ChartToolbarProps) {
  const { t } = useTranslation();

  const aggregations: { value: Aggregation; label: string }[] = [
    {
      value: "week",
      label: t("components.demandChart.toolbar.aggregation.week"),
    },
    {
      value: "month",
      label: t("components.demandChart.toolbar.aggregation.month"),
    },
    {
      value: "quarter",
      label: t("components.demandChart.toolbar.aggregation.quarter"),
    },
  ];
  const metrics: { value: Metric; label: string }[] = [
    {
      value: "quantity",
      label: t("components.demandChart.toolbar.metric.quantity"),
    },
    {
      value: "revenue",
      label: t("components.demandChart.toolbar.metric.revenue"),
    },
  ];
  const modes: { value: Mode; label: string }[] = [
    { value: "raw", label: t("components.demandChart.toolbar.mode.raw") },
    {
      value: "cleansed",
      label: t("components.demandChart.toolbar.mode.cleansed"),
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
      <Section label={t("components.demandChart.toolbar.section.range")}>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={activePreset ?? ""}
          onValueChange={(v) => v && onPreset(v as Preset)}
        >
          {PRESETS.map((p) => (
            <ToggleGroupItem key={p} value={p}>
              {p}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </Section>
      <Section label={t("components.demandChart.toolbar.section.period")}>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={aggregation}
          onValueChange={(v) => v && onAggregation(v as Aggregation)}
        >
          {aggregations.map((a) => (
            <ToggleGroupItem key={a.value} value={a.value}>
              {a.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </Section>
      {metric && onMetric && (
        <Section label={t("components.demandChart.toolbar.section.metric")}>
          <ToggleGroup
            type="single"
            variant="outline"
            size="sm"
            value={metric}
            onValueChange={(v) => v && onMetric(v as Metric)}
          >
            {metrics.map((m) => (
              <ToggleGroupItem key={m.value} value={m.value}>
                {m.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </Section>
      )}
      <Section label={t("components.demandChart.toolbar.section.mode")}>
        <ToggleGroup
          type="single"
          variant="outline"
          size="sm"
          value={mode}
          onValueChange={(v) => v && onMode(v as Mode)}
        >
          {modes.map((m) => (
            <ToggleGroupItem key={m.value} value={m.value}>
              {m.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </Section>
    </div>
  );
}
